import { defineStore } from 'pinia'
import { computed } from 'vue'
import { v4 as generateUuid } from 'uuid'
import fuzzysort from 'fuzzysort'
import chunk from 'lodash/chunk'

import { SfLog } from '@app/adapters/logger'
import { isResponseError } from '@app/utils/predicates'
import { difference } from '@app/utils/set-helpers'
import api from '@app/insights/api'
import {
  abortChunkFetches,
  fetchChunkRowsWithRetry,
  fetchChunksInBatches,
  getChunkFetchSignal,
} from '@app/insights/services/chunk-results-fetcher'
import { useInsightsRootStore } from '../insights-root'
import { CHUNKS_PER_REQUEST, MAX_RETRIES } from '../constants'
import { useColumnsStore } from '../columns'
import { useFiltersStore } from '../filters'
import { useSegmentsStore } from '../segments'
import { useAccountsListDrawer } from '../accounts-list-drawer'
import { trackRecommendedQueryLoaded } from '../../utils/track-recommended-query'

import { useCompaniesDataStore } from './data'
import { useCompaniesSelectionStore } from './selection'
import { useCompaniesRequestLifecycleStore } from './request-lifecycle'
import { useCompaniesErrorsStore } from './errors'
import { useCompaniesUiStore } from './ui'
import type { ChunkData, ChunkRow } from './types'
import I18n from '@app/adapters/I18n'
import type { PageQuery } from '@app/shared/pinia/insights-page-run'

// handleBrowserError in sfetch/raw.ts maps both genuine aborts and real
// network failures to ResponseError{status: 0}. Use the signal's aborted flag
// to disambiguate.
function isAbortError(
  error: unknown,
  signal: AbortSignal | null | undefined
): boolean {
  if (!error) return false
  if ((error as { name?: string }).name === 'AbortError') return true
  return (
    isResponseError(error) && error.status === 0 && signal?.aborted === true
  )
}

// raw.ts answers a 401 with a Turbolinks reload, so the session expiry is
// already being handled - reporting it would flash an error over the page for
// the moment before the reload lands.
function isSessionExpiry(error: unknown): boolean {
  return isResponseError(error) && error.status === 401
}

export const useCompaniesStore = defineStore('insights-companies', () => {
  // Sub-store resolution relies on Pinia being active at call time (enforced by createApp). Do not invoke useCompaniesStore() at module import time.
  const dataStore = useCompaniesDataStore()
  const selectionStore = useCompaniesSelectionStore()
  const lifecycleStore = useCompaniesRequestLifecycleStore()
  const errorsStore = useCompaniesErrorsStore()
  const uiStore = useCompaniesUiStore()
  const columnsStore = useColumnsStore()
  const segmentsStore = useSegmentsStore()

  // ---- Composed state passthrough ----
  const companies = computed(() => dataStore.companies)
  const allCompanyIds = computed(() => dataStore.allCompanyIds)
  const collectedCompanyIds = computed(() => dataStore.collectedCompanyIds)
  const selectedCompanies = computed(() => selectionStore.selectedCompanies)
  const pendingCompaniesRequest = computed(
    () => lifecycleStore.pendingCompaniesRequest
  )
  const segmentRunDone = computed(() => lifecycleStore.segmentRunDone)
  const companiesFullyLoaded = computed(
    () => lifecycleStore.companiesFullyLoaded
  )
  const companiesFullyLoadedAt = computed(
    () => lifecycleStore.companiesFullyLoadedAt
  )
  const inFlightChunkFetches = computed(
    () => lifecycleStore.inFlightChunkFetches
  )
  const getError = computed(() => errorsStore.error)
  const runStartFailed = computed(() => errorsStore.runStartFailed)
  const retries = computed(() => errorsStore.retries)
  const searchTerm = computed(() => uiStore.searchTerm)
  const didAutoSortOnFilterError = computed(
    () => uiStore.didAutoSortOnFilterError
  )

  // ---- Derived getters ----
  const allCompaniesCount = computed(() =>
    dataStore.allCompanyIds === null ? 0 : dataStore.allCompanyIds.size
  )

  const companiesProcessedPercentage = computed(() => {
    const count = allCompaniesCount.value

    if (count === 0) {
      return dataStore.allCompanyIds === null ? 0 : 100
    }
    if (count < dataStore.collectedCompanyIds.size) {
      return 0
    }
    return Math.floor((dataStore.collectedCompanyIds.size / count) * 100)
  })

  const allCompaniesProcessed = computed(
    () => companiesProcessedPercentage.value === 100
  )

  const retriesExhausted = computed(() => errorsStore.retries > MAX_RETRIES)

  // The page's query is still running. Not the same as having every company
  // on screen: slow filters run in a second pass that fills columns of rows
  // already listed, and the run stays open on the server until that pass ends
  // too. The navbar query picker reads this same condition, so a spinner there
  // and one on the page cannot disagree. Nothing is running until the page has
  // asked for results.
  const isLoadingResults = computed(
    () =>
      lifecycleStore.pendingCompaniesRequest !== null &&
      !companiesFullyLoaded.value &&
      !getError.value
  )

  // The query the page is showing, on the same terms the page's own URL states
  // it (navigation.ts#stateToUrlParams): a saved query while its filters are
  // untouched, "All clients" while nothing is filtered, and nothing at all
  // once the filters no longer match a saved query.
  const pageQuery = computed<PageQuery | null>(() => {
    const segment = segmentsStore.segment

    if (segment && !segmentsStore.isSegmentModified) {
      return segment.partner_segment_id
        ? { segmentId: null, partnerSegmentId: segment.partner_segment_id }
        : { segmentId: segment.id, partnerSegmentId: null }
    }

    if (!segment && useFiltersStore().getActiveFilters.length === 0) {
      return { segmentId: null, partnerSegmentId: null }
    }

    return null
  })

  const getAllCompanies = computed(() => Object.values(dataStore.companies))

  // Separated into two layers so that chunk appends only re-sort and search
  // independently: sort reruns when companies/sortColumn/sortType change;
  // fuzzy search reruns only when sortedCompanies or searchTerm changes.
  const sortedCompanies = computed(() =>
    columnsStore.getColumnSortMethod(dataStore.companies)
  )

  const sortedAndFilteredCompanies = computed(() => {
    if (uiStore.searchTerm?.length) {
      const normalizedSearchTerm = uiStore.searchTerm.toLowerCase()
      return fuzzysort
        .go(normalizedSearchTerm, sortedCompanies.value, {
          keys: ['filter_values.name.formatted_value'],
        })
        .map((res) => res.obj)
    }
    return sortedCompanies.value
  })

  const errorCountsPerFilter = computed((): Record<string, number> => {
    // Gate on didAutoSortOnFilterError to avoid O(N·F) walk on every chunk
    // append when no filter error has been detected yet.
    if (!uiStore.didAutoSortOnFilterError) return {}
    return getAllCompanies.value.reduce(
      (accumulator: Record<string, number>, company) => {
        for (const [key, filter] of Object.entries(
          (
            company as {
              filter_values: Record<string, { filter_error?: unknown }>
            }
          ).filter_values
        )) {
          if (filter.filter_error) {
            accumulator[key] ||= 0
            accumulator[key] += 1
          }
        }
        return accumulator
      },
      {}
    )
  })

  const selectedLedgers = computed<(number | string)[]>(() => {
    const result: (number | string)[] = []
    for (const key of Object.keys(dataStore.companies)) {
      const id = Number(key)
      if (!selectionStore.selectedCompanies.has(id)) continue
      const ledgerId = dataStore.companies[id].filter_values.selectedPeriod?.id
      if (ledgerId !== null && ledgerId !== undefined) result.push(ledgerId)
    }
    return result
  })

  // ---- Internal helpers ----
  function isCurrentRequest(requestRef: string): boolean {
    return lifecycleStore.pendingCompaniesRequest?.ref === requestRef
  }

  // requestRef is omitted by the trailing .catch blocks: those only ever see
  // exceptions raised by our own handlers, which must stay visible even though
  // the handlers themselves may already have moved the pending ref on.
  function handleRequestFailure({
    context,
    error,
    signal = null,
    requestRef = null,
    runStartFailed,
  }: {
    context: string
    error: unknown
    signal?: AbortSignal | null
    requestRef?: string | null
    runStartFailed: boolean
  }): void {
    if (isAbortError(error, signal) || isSessionExpiry(error)) return
    if (requestRef !== null && !isCurrentRequest(requestRef)) return

    SfLog.error(`${context} failed`, { extra: { error } })

    const message = I18n.t('companies_overview.filters.generic_filter_error')

    if (runStartFailed) {
      errorsStore.setRunStartFailed(message)
    } else {
      errorsStore.setError(message)
    }
  }

  // One append per response: every append re-sorts and re-renders the list.
  // The server answers 404 while none of the rows exists, which the fetch
  // retries. Rows that are still missing go in a second request.
  async function fetchAndApplyChunks({
    requestRef,
    chunkIndexes,
  }: {
    requestRef: string
    chunkIndexes: number[]
  }): Promise<void> {
    if (!isCurrentRequest(requestRef)) {
      return
    }

    lifecycleStore.incrementInFlight()

    try {
      const signal = getChunkFetchSignal()
      let rows: ChunkRow[] | null = null
      let fetchError: unknown = null

      try {
        rows = await fetchChunkRowsWithRetry({
          requestRef,
          chunkIndexes,
          signal,
        })
      } catch (err) {
        if (isAbortError(err, signal)) {
          return
        }
        fetchError = err
      }

      if (!isCurrentRequest(requestRef)) {
        return
      }

      const requested = (rows ?? []).filter((row) =>
        chunkIndexes.includes(row.chunk_index)
      )
      // A response without a requested row would ask again forever.
      if (fetchError || requested.length === 0) {
        SfLog.error('fetchChunkRowsWithRetry failed', {
          extra: { requestRef, chunkIndexes, error: fetchError },
        })
        chunkIndexes.forEach((index) => lifecycleStore.markChunkDone(index))
        return
      }

      applyChunkData({
        matches: requested.flatMap((row) => row.results.matches ?? []),
        non_matches: requested.flatMap((row) => row.results.non_matches ?? []),
      })
      const received = new Set(requested.map((row) => row.chunk_index))
      received.forEach((index) => lifecycleStore.markChunkDone(index))
      const missing = chunkIndexes.filter((index) => !received.has(index))
      if (missing.length > 0) {
        await fetchAndApplyChunks({ requestRef, chunkIndexes: missing })
      }
    } finally {
      lifecycleStore.decrementInFlight()
    }
  }

  function applyChunkData(chunkData: ChunkData): void {
    const matchedIds = dataStore.appendChunk(
      chunkData,
      () => uiStore.didAutoSortOnFilterError,
      (filterKey: string) => {
        uiStore.didAutoSortOnFilterError = true
        columnsStore.setSortColumn({ sortColumn: filterKey })
      }
    )
    selectionStore.autoSelectInitial(matchedIds)
  }

  // ---- Actions ----
  function initCompaniesRequest(requestRef: string): void {
    dataStore.reset()
    selectionStore.reset()
    lifecycleStore.initRequest(requestRef)
    errorsStore.reset()
    uiStore.reset()
  }

  async function receivedQueryResults(data: {
    request_ref: string
    error?: string
    segment_run_done?: boolean
    finished_at?: number
    chunk_done?: number
    chunks_done?: number[]
  }): Promise<void> {
    if (!isCurrentRequest(data['request_ref'])) return

    if (data['error']) {
      errorsStore.setError(data['error'])
      return
    }

    if (data['segment_run_done']) {
      lifecycleStore.markSegmentRunDone()
      lifecycleStore.setFullyLoadedAt(data['finished_at'])
      checkIfMissingAnyCompanies()
      await maybeMarkFullyLoaded()
      return
    }

    let chunkIndices: number[] = []
    if (Array.isArray(data['chunks_done'])) {
      chunkIndices = data['chunks_done']
    } else if (data['chunk_done'] !== undefined) {
      chunkIndices = [data['chunk_done']]
    }

    if (chunkIndices.length === 0) return

    const requestRef = data['request_ref']
    await fetchChunksInBatches(
      chunk(chunkIndices, CHUNKS_PER_REQUEST),
      (chunkIndexes: number[]) =>
        fetchAndApplyChunks({ requestRef, chunkIndexes })
    )
    checkIfMissingAnyCompanies()
    await maybeMarkFullyLoaded()
  }

  async function maybeMarkFullyLoaded(): Promise<void> {
    const pending = lifecycleStore.pendingCompaniesRequest
    if (!pending) return

    const { chunkCount, receivedChunkIds } = pending
    const allChunksReceived =
      chunkCount === 0 ||
      (chunkCount > 0 && receivedChunkIds.size >= chunkCount)

    if (
      lifecycleStore.segmentRunDone &&
      allChunksReceived &&
      lifecycleStore.inFlightChunkFetches === 0 &&
      !lifecycleStore.companiesFullyLoaded
    ) {
      lifecycleStore.markFullyLoaded()
      trackRecommendedQueryLoaded(segmentsStore.segment)
      await segmentsStore.silentReloadCurrentSegment()
      updateSearchTerm(uiStore.searchTerm)
    }
  }

  function checkIfMissingAnyCompanies(): void {
    if (dataStore.allCompanyIds === null) {
      return
    }
    // A reopened run that still works announces every chunk that has saved part
    // of its clients, so a client is missing only after segment_run_done. A retry
    // before that starts a second run beside the working one.
    if (!lifecycleStore.segmentRunDone) {
      return
    }
    if (lifecycleStore.inFlightChunkFetches > 0) {
      return
    }

    const pending = lifecycleStore.pendingCompaniesRequest
    if (!pending) {
      return
    }

    // The slow pass announces chunks of its own, so a query with slow filters
    // receives more chunk ids than the fast pass made.
    const { receivedChunkIds, chunkCount } = pending
    if (receivedChunkIds.size < chunkCount) {
      return
    }

    // A client that the page has and allCompanyIds lacks is not missing. A job
    // can save clients after its run finished, and a cache hit then leaves them
    // out of allCompanyIds, which no retry changes.
    const missingIds = difference(
      dataStore.allCompanyIds,
      dataStore.collectedCompanyIds
    )
    if (missingIds.size === 0) {
      return
    }

    if (retriesExhausted.value) {
      errorsStore.setError(
        I18n.t('companies_overview.filters.generic_filter_error')
      )
      SfLog.info(
        'Received all chunks done, but missing results for some companies',
        {
          extra: {
            missingIds: Array.from(missingIds),
            missingIdsCount: missingIds.size,
          },
        }
      )
      return
    }

    void fetchMissingCompanies(Array.from(missingIds))
  }

  function fetchFilteredCompanies({
    existingRequestRef = null,
    noCache = false,
  }: {
    existingRequestRef?: string | null
    noCache?: boolean
  } = {}): Promise<void> {
    const piniaRootStore = useInsightsRootStore()

    if (useFiltersStore().hasLedgerSpecificFiltersWithoutPeriodSelection) {
      void segmentsStore.setDefaultPeriod()
    }

    // A manual refresh (noCache) reloads the whole query, so refetch the
    // accounts-list drawer's current cell too, keeping its content in sync.
    if (noCache) {
      useAccountsListDrawer().refresh()
    }

    const requestRef =
      existingRequestRef ||
      (segmentsStore.lastSegmentRef ?? null) ||
      generateUuid()
    const pendingRequestRef = lifecycleStore.pendingCompaniesRequest?.ref

    abortChunkFetches()
    const signal = getChunkFetchSignal()
    initCompaniesRequest(requestRef)

    const filtersStore = useFiltersStore()
    const segment = segmentsStore.segment
    return api
      .fetchFilteredCompanies({
        rootState: piniaRootStore,
        activeFilters: filtersStore.activeFilters,
        allFiltersByType: filtersStore.allFiltersByType,
        segmentId: !segmentsStore.isSegmentModified && segment?.id,
        partnerSegmentId:
          !segmentsStore.isSegmentModified && segment?.partner_segment_id,
        pendingRequestRef,
        requestRef,
        noCache,
        signal,
      })
      .then(
        (
          data: {
            chunkCount: string
            allCompanyIds?: number[]
            requestRef?: string
          } | null
        ) => {
          if (!data || !isCurrentRequest(requestRef)) return

          const canonicalRef = data.requestRef || requestRef
          if (canonicalRef !== requestRef) {
            lifecycleStore.adoptRequestRef({
              from: requestRef,
              to: canonicalRef,
            })
          }

          lifecycleStore.setPending({
            requestRef: canonicalRef,
            chunkCount: parseInt(data.chunkCount),
          })
          if (data.allCompanyIds) {
            dataStore.setAllCompanyIds(data.allCompanyIds)
          }
          checkIfMissingAnyCompanies()
          void maybeMarkFullyLoaded()
        },
        (error: unknown) =>
          handleRequestFailure({
            context: 'fetchFilteredCompanies',
            error,
            signal,
            requestRef,
            runStartFailed: true,
          })
      )
      .catch((error: unknown) =>
        handleRequestFailure({
          context: 'fetchFilteredCompanies handler',
          error,
          runStartFailed: true,
        })
      )
  }

  function fetchMissingCompanies(missingCompanyIds: number[]): Promise<void> {
    if (retriesExhausted.value) return Promise.resolve()

    const piniaRootStore = useInsightsRootStore()

    if (useFiltersStore().hasLedgerSpecificFiltersWithoutPeriodSelection) {
      void segmentsStore.setDefaultPeriod()
    }

    // Under the saved run's ref, the server serves the finished run from the
    // cache, and no job calculates the missing clients.
    const requestRef = generateUuid()
    const pendingRequestRef = lifecycleStore.pendingCompaniesRequest?.ref

    abortChunkFetches()
    const signal = getChunkFetchSignal()
    errorsStore.incrementRetries()
    lifecycleStore.initRetryRequest(requestRef)

    const filtersStore = useFiltersStore()
    return api
      .fetchFilteredCompaniesRetry({
        rootState: piniaRootStore,
        activeFilters: filtersStore.activeFilters,
        allFiltersByType: filtersStore.allFiltersByType,
        pendingRequestRef,
        requestRef,
        missingCompanyIds,
        signal,
      })
      .then(
        (data: { chunkCount: string } | null) => {
          if (!data || !isCurrentRequest(requestRef)) return

          lifecycleStore.setPending({
            requestRef,
            chunkCount: parseInt(data.chunkCount),
          })
          checkIfMissingAnyCompanies()
          void maybeMarkFullyLoaded()
        },
        (error: unknown) =>
          handleRequestFailure({
            context: 'fetchMissingCompanies',
            error,
            signal,
            requestRef,
            runStartFailed: false,
          })
      )
      .catch((error: unknown) =>
        handleRequestFailure({
          context: 'fetchMissingCompanies handler',
          error,
          runStartFailed: false,
        })
      )
  }

  function selectCompany(companyId: number): void {
    selectionStore.markUserTouched()
    selectionStore.add(companyId)
  }

  function deselectCompany(companyId: number): void {
    selectionStore.markUserTouched()
    selectionStore.remove(companyId)
  }

  function updateSearchTerm(value: string | null): void {
    const searchTermChanged = uiStore.searchTerm !== value
    uiStore.setSearchTerm(value)
    const filteredIds = new Set(
      sortedAndFilteredCompanies.value.map((c) => (c as { id: number }).id)
    )

    if (searchTermChanged) {
      selectionStore.replaceWith(filteredIds)
    } else {
      selectionStore.retainIntersection(filteredIds)
    }
  }

  return {
    // State passthrough
    companies,
    allCompanyIds,
    collectedCompanyIds,
    selectedCompanies,
    pendingCompaniesRequest,
    segmentRunDone,
    companiesFullyLoaded,
    companiesFullyLoadedAt,
    inFlightChunkFetches,
    retries,
    searchTerm,
    didAutoSortOnFilterError,
    // Derived getters
    allCompaniesCount,
    companiesProcessedPercentage,
    allCompaniesProcessed,
    isLoadingResults,
    pageQuery,
    retriesExhausted,
    getAllCompanies,
    sortedAndFilteredCompanies,
    getError,
    runStartFailed,
    errorCountsPerFilter,
    selectedLedgers,
    // Actions
    initCompaniesRequest,
    receivedQueryResults,
    maybeMarkFullyLoaded,
    checkIfMissingAnyCompanies,
    fetchFilteredCompanies,
    fetchMissingCompanies,
    selectCompany,
    deselectCompany,
    updateSearchTerm,
  }
})
